import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Trophy, Medal, TrendUp, TrendDown } from "@phosphor-icons/react";

const PERIOD_LABEL = { week: "This Week", month: "This Month", quarter: "This Quarter", year: "This Year" };

export default function Performance() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get(`/performance/rankings?period=${period}`);
    setData(data);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>;

  const Card = ({ title, rows, tone = "sky", icon: Icon = Trophy }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={`text-${tone}-600`} />
        <h4 className="font-semibold text-sm">{title}</h4>
      </div>
      <ol className="space-y-2">
        {rows.length === 0 && <li className="text-xs text-slate-400 py-3 text-center">Not enough data.</li>}
        {rows.map((r, i) => (
          <li key={r.employee_id} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-6 h-6 rounded-full bg-${tone}-100 text-${tone}-700 grid place-items-center text-xs font-bold`}>{i + 1}</div>
              <div className="truncate">
                <div className="font-medium truncate">{r.name}</div>
                <div className="text-xs text-slate-500">{r.team || "—"}</div>
              </div>
            </div>
            <div className="text-right text-xs">
              <div className="font-semibold">{r.score}</div>
              <div className="text-slate-500">apps {r.applications} · iv {r.interviews} · offers {r.offers}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2"><Trophy size={22} /> Performance</h2>
          <p className="text-sm text-slate-500">Rankings by weighted score: applications + 3× interviews + 5× confirmed + 10× offers + 25× placements.</p>
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="perf-period"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          {Object.entries(PERIOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={`Overall Top 3 — ${PERIOD_LABEL[period]}`} rows={data.overall.top3} tone="emerald" icon={Medal} />
        <Card title={`Overall Bottom 3 — ${PERIOD_LABEL[period]}`} rows={data.overall.bottom3} tone="red" icon={TrendDown} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Team Rankings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.teams).map(([team, t]) => (
            <div key={team} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`team-${team}`}>
              <div className="font-semibold text-sm mb-2">{team} · {t.count} people</div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 mb-1">Top 3</div>
                  {t.top3.map((r, i) => (
                    <div key={r.employee_id} className="flex justify-between text-xs py-0.5">
                      <span>{i+1}. {r.name}</span><span className="tabular-nums text-slate-500">{r.score}</span>
                    </div>
                  ))}
                </div>
                {t.bottom3.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-red-600 mb-1">Bottom 3</div>
                    {t.bottom3.map((r, i) => (
                      <div key={r.employee_id} className="flex justify-between text-xs py-0.5">
                        <span>{i+1}. {r.name}</span><span className="tabular-nums text-slate-500">{r.score}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-slate-100 font-semibold text-sm">Full leaderboard</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Rank</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Team</th>
              <th className="text-right px-3 py-2">CP</th><th className="text-right px-3 py-2">EA</th><th className="text-right px-3 py-2">Cold</th>
              <th className="text-right px-3 py-2">IV</th><th className="text-right px-3 py-2">Conf</th><th className="text-right px-3 py-2">Off</th>
              <th className="text-right px-3 py-2">Plc</th><th className="text-right px-3 py-2">Target %</th><th className="text-right px-3 py-2">Incentives</th>
              <th className="text-right px-3 py-2">Score</th>
            </tr>
          </thead>
          <tbody>
            {data.all.map((r, i) => (
              <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rank-${r.employee_id}`}>
                <td className="px-3 py-2 font-semibold">{i+1}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.team || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.career_portal}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.easy_apply}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.cold_emails}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.interviews}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.confirmed_interviews}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.offers}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.placements}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.target_achievement_pct}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.incentives_earned}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
